import { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';

import styles from './Dashboard.module.css';
import MainContentPending from '../../pageStructure/mainContentPending';
import PendingView from './pendingView/PendingView';
import { FormattedName } from '../../../iwIntlProvider/FormattedName';
import { RootState } from '../../../iwProvider/configureStore';
import {
    ACTIVATE_IWOCAPAY_DASHBOARD,
    REFRESH_APPLICATIONS,
    SELECT_NEW_PLAN
} from './Dashboard.actions';
import { Dispatch } from 'redux';
import OverdueView from './approvedView/OngoingLoanView/OverdueView/OverdueView';
import OngoingLoanView from './approvedView/OngoingLoanView/OngoingLoanView';
import { PaymentPlanCard } from './paymentPlanCard/PaymentPlanCard';
import NothingSelectedView from './NothingSelectedView/NothingSelectedView';
import PendingApplication from './applicationCard/pendingApplication/PendingApplication';
import { ApprovedApplication } from './applicationCard/approvedApplication/ApprovedApplication';
import ExpiredApplication from './applicationCard/expiredApplication/ExpiredApplication';
import { DeclinedApplication } from './applicationCard/declinedApplication/DeclinedApplication';
import SelectedView from './SelectedView/SelectedView';
import PlanButton from './PlanButton/PlanButton';
import PaymentPlans from './PaymentPlans/PaymentPlans';
import EndedPaymentPlanView from './endedView/EndedPaymentPlanView';
import { ExclamationMarkOutlineSVG } from '../../../svgComponents/exclamationMarkOutlineSVG/ExclamationMarkOutlineSVG';
import { CrossSellCard } from './CrossSellCard/CrossSellCard';
import { Plan } from './Dashboard.types';
import { CancelledView } from './cancelledView/CancelledView';
import ANTCard from './ANTCard/ANTCard';
import { SLSecurityCheckBanner } from './SLSecurityCheckBanner/SLSecurityCheckBanner';
import { NoPlansView } from './NoPlansView/NoPlansView';
import { tracking } from '@iwoca/frontend-tracking-library';
import SpendingLimitCard from './spendingLimitCard/SpendingLimitCard';
import { SpendingLimitDrawer } from './SpendingLimitDrawer/SpendingLimitDrawer';
import { ScheduledView } from './scheduledView/ScheduledView';
import { useStateKey } from '../../../iwQueryClient/useStateKey';
import { trackPageView } from '../../../tracking/pageView';
import { useRefreshSpendingLimits } from './hooks/useRefreshSpendingLimits';
import { PartialView } from './PartialView';
import { SellerDirectoryBannerTest } from './SellerDirectoryBanner/SellerDirectoryBannerTest';
import { useMediaQuery } from '../../../customHooks/useMediaQuery';
import { useSellerDirectory } from '../sellerDirectory/useSellerDirectory';
import { SellerDirectoryBanner } from './SellerDirectoryBanner/SellerDirectoryBanner';
import { BuyerMultiAccessBanner } from './BuyerMultiAccessBanner/BuyerMultiAccessBanner';

const mapStateToProps = (state: RootState) => state.iwocapayDashboard;

const mapDispatchToProps = (dispatch: Dispatch) => ({
    setSelectedPaymentPlan: (plan: Plan) => {
        dispatch({
            type: SELECT_NEW_PLAN,
            plan
        });
    },
    activate: () => dispatch({ type: ACTIVATE_IWOCAPAY_DASHBOARD }),
    refreshApplications: () => dispatch({ type: REFRESH_APPLICATIONS })
});

type PropsFromRedux = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

function OverdueBanner() {
    return (
        <div className={styles.paymentOverdueBanner}>
            <div className={styles.paymentOverdueBannerTitle}>
                <ExclamationMarkOutlineSVG
                    className={styles.paymentOverdueBannerIcon}
                />
                You have some overdue payments
            </div>
            <div className={styles.paymentOverdueBannerBody}>
                You’ll need to settle any overdue payments before you can spread
                the cost of a new one.
            </div>
        </div>
    );
}

function Dashboard({
    activate,
    ready,
    error,
    buyerFirstName,
    application,
    selectedPlan,
    creditLimit,
    setSelectedPaymentPlan,
    ongoingPaymentPlans,
    capturedPlans,
    cancelledPayments,
    iwocaPayApplication,
    rawApplication,
    endedPayments,
    refreshApplications,
    isSoleTrader
}: PropsFromRedux) {
    const stateKey = useStateKey();
    const isMobile = useMediaQuery('(max-width: 1023px)');
    const { sellers } = useSellerDirectory();
    const [isSpendingLimitDrawerOpen, setIsSpendingLimitDrawerOpen] =
        useState(false);

    const selectPaymentPlan = useCallback(
        (plan: Plan) => setSelectedPaymentPlan(plan),
        [setSelectedPaymentPlan]
    );

    const {
        refreshSpendingLimit,
        isLoading: loadingRefreshSpendingLimit,
        isError: refreshSpendingLimitError
    } = useRefreshSpendingLimits(refreshApplications);

    const handleRefreshSpendingLimit = async () => {
        try {
            await refreshSpendingLimit();
        } catch (e) {}
    };

    useEffect(() => {
        if (stateKey) {
            trackPageView(stateKey, 'Apply Flexi Loan');
        }
    }, [stateKey]);

    useEffect(() => {
        activate();
    }, [activate]);

    useEffect(() => {
        if (selectedPlan) {
            selectPaymentPlan(selectedPlan);
        }
    }, [selectedPlan, selectPaymentPlan]);

    useEffect(() => {
        if (!selectedPlan?.status) return;

        tracking.ampli.buyerDashboardViewed({
            buyer_dashboard_state: selectedPlan.status
        });
    }, [selectedPlan?.status]);

    if (!ready && !error) {
        return <MainContentPending />;
    }

    const renderIwocaPayApplicationFallback = () => {
        if (!iwocaPayApplication) return null;

        switch (iwocaPayApplication!.status) {
            case 'declined':
                return <DeclinedApplication />;
            default:
                return <NoPlansView creditLimit={creditLimit} />;
        }
    };

    const renderSelectedComponent = () => {
        if (!selectedPlan) return <NothingSelectedView />;

        switch (selectedPlan.status) {
            case 'overdue':
                return <OverdueView paymentPlan={selectedPlan} />;
            case 'ongoing':
                return <OngoingLoanView paymentPlan={selectedPlan} />;
            case 'pending':
                return <PendingApplication />;
            case 'partial':
                return <PartialView application={selectedPlan} />;
            case 'approved':
                return <ApprovedApplication application={selectedPlan} />;
            case 'expired':
                return <ExpiredApplication />;
            case 'declined':
                return <DeclinedApplication application={selectedPlan} />;
            case 'ended':
                return <EndedPaymentPlanView />;
            case 'scheduled':
                return <ScheduledView plan={selectedPlan} />;
            case 'cancelled':
                return <CancelledView plan={selectedPlan} />;
            default:
                return null;
        }
    };

    const showSellerDirectoryBannerTest =
        rawApplication?.status !== 'declined' && isSoleTrader;

    const showSellerDirectoryLink =
        sellers &&
        rawApplication?.status !== 'declined' &&
        isSoleTrader === false; // isSoleTrader === null must not pass

    const isCrossSellEligible = (): boolean => {
        if (isOverdue) return false;
        if (capturedPlans.length > 0) return false;
        if (rawApplication && rawApplication.status === 'declined')
            return false;
        if (selectedPlan && selectedPlan.status === 'pending') return false;

        return true;
    };

    const isOverdue = ongoingPaymentPlans.some(
        (paymentPlan) => paymentPlan.status === 'overdue'
    );
    const hasPaymentPlans =
        application?.payLink ||
        capturedPlans.length > 0 ||
        ongoingPaymentPlans.length > 0 ||
        cancelledPayments.length > 0 ||
        endedPayments.length > 0;

    return (
        <div className={styles.iwocaPayDashboard}>
            <div className={styles.iwocaPayDashboardContent}>
                {ready ? (
                    <>
                        <section className={styles.welcome}>
                            <div>
                                {isOverdue && <OverdueBanner />}
                                <ANTCard />
                                <SLSecurityCheckBanner />
                                {isCrossSellEligible() && <CrossSellCard />}
                                <SpendingLimitDrawer
                                    isOpen={isSpendingLimitDrawerOpen}
                                    setIsOpen={setIsSpendingLimitDrawerOpen}
                                    refreshSpendingLimit={
                                        handleRefreshSpendingLimit
                                    }
                                    loadingRefreshSpendingLimit={
                                        loadingRefreshSpendingLimit
                                    }
                                    isError={refreshSpendingLimitError}
                                    application={iwocaPayApplication}
                                />
                            </div>
                            <h1>
                                Welcome{' '}
                                <FormattedName firstName={buyerFirstName} />!
                            </h1>
                            <SpendingLimitCard
                                setIsSpendingLimitDrawerOpen={
                                    setIsSpendingLimitDrawerOpen
                                }
                                refreshSpendingLimit={
                                    handleRefreshSpendingLimit
                                }
                                loadingRefreshSpendingLimit={
                                    loadingRefreshSpendingLimit
                                }
                                application={iwocaPayApplication}
                            />
                        </section>
                        <section className={styles.banners}>
                            {showSellerDirectoryBannerTest && (
                                <SellerDirectoryBannerTest />
                            )}
                            {showSellerDirectoryLink && (
                                <SellerDirectoryBanner />
                            )}
                            <BuyerMultiAccessBanner />
                        </section>
                        <section className={styles.application}>
                            {application && (
                                <div className={styles.outstandingApplications}>
                                    <h3>You have an outstanding application</h3>
                                    <PlanButton
                                        status={application?.status}
                                        selectedPlanId={
                                            selectedPlan?.pay_link_id!
                                        }
                                        selectPaymentPlan={selectPaymentPlan}
                                        paymentPlan={application}
                                        renderContent={renderSelectedComponent}
                                    >
                                        <PaymentPlanCard
                                            created={
                                                application.payLink.created
                                            }
                                            reference={
                                                application.payLink.reference
                                            }
                                            sellerName={
                                                application.payLink
                                                    .seller_trading_name
                                            }
                                            amount={application.payLink.amount}
                                            status={application.status}
                                        />
                                    </PlanButton>
                                </div>
                            )}
                        </section>
                        {hasPaymentPlans ? (
                            <>
                                <section className={styles.plans}>
                                    <PaymentPlans
                                        selectPaymentPlan={selectPaymentPlan}
                                        renderContent={renderSelectedComponent}
                                    />
                                </section>
                                {!isMobile && (
                                    <section className={styles.plan}>
                                        <SelectedView plan={selectedPlan}>
                                            {renderSelectedComponent()}
                                        </SelectedView>
                                    </section>
                                )}
                            </>
                        ) : (
                            renderIwocaPayApplicationFallback()
                        )}
                    </>
                ) : (
                    <PendingView />
                )}
            </div>
        </div>
    );
}

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);
